import { useCallback } from 'react';
import { useAtom } from 'jotai';
import { differenceInDays, isAfter, startOfDay, subDays } from 'date-fns';
import { enUS, ko } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Button, debounce, Stack, Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';

import type { PrimitiveAtom } from 'jotai';
import type { Period } from '@/@types/device';

interface DatePickerProps {
  periodAtom: PrimitiveAtom<Period>;
  showShortcut?: boolean;
}

export default function DatePicker({ periodAtom, showShortcut = false }: DatePickerProps) {
  const [period, setPeriod] = useAtom(periodAtom);
  const { t, i18n } = useTranslation();
  const isKorean = i18n.language.startsWith('ko');

  const calendarFieldFormat = isKorean ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';
  const calenderHeaderFormat = isKorean ? 'yyyy년 M월' : 'MMMM yyyy';

  function onShortcutClick(key: string) {
    const now = new Date();
    if (key === 'today') {
      setPeriod({ from: startOfDay(now), to: now });
    } else if (key === 'last7Days') {
      setPeriod({ from: subDays(now, 7), to: now });
    } else if (key === 'last30Days') {
      setPeriod({ from: subDays(now, 30), to: now });
    }
  }

  const Picker = useCallback(
    ({ periodKey, value }: { periodKey: keyof Period; value: Date }) => {
      let updated = false;
      function onAccept(key: keyof Period, date: Date) {
        if (updated) return;
        updated = true;
        setPeriod((prev) => {
          let error = false;
          if (key === 'from' && isAfter(date, prev.to)) {
            error = true;
            window.alert(t('Devices.message.wrongFromDate'));
          } else if (key === 'from' && differenceInDays(prev.to, date) > 30) {
            error = true;
            window.alert(t('Devices.message.over30Days'));
          } else if (key === 'to' && isAfter(prev.from, date)) {
            error = true;
            window.alert(t('Devices.message.wrongToDate'));
          } else if (key === 'to' && differenceInDays(date, prev.from) > 30) {
            error = true;
            window.alert(t('Devices.message.over30Days'));
          }

          // Create new Date object to trigger re-render
          if (error) return { from: new Date(prev.from), to: new Date(prev.to) };
          return { ...prev, [key]: date };
        });
      }
      const onChange = debounce(onAccept, 1000);

      return (
        <DateTimePicker
          label={t(`Devices.label.${periodKey}`)}
          value={value}
          onChange={(e) => onChange(periodKey, e)}
          onAccept={(e) => onAccept(periodKey, e)}
          ampm
          disableFuture
          format={calendarFieldFormat}
          slotProps={{ calendarHeader: { format: calenderHeaderFormat } }}
          sx={{ bgcolor: 'white' }}
        />
      );
    },
    [calendarFieldFormat, calenderHeaderFormat, setPeriod, t],
  );

  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" gap={2}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={isKorean ? ko : enUS}
          localeText={{ okButtonLabel: t('Common.label.ok') }}
        >
          <Picker value={period.from} periodKey="from" />
          <Typography fontWeight="bold"> ~ </Typography>
          <Picker value={period.to} periodKey="to" />
        </LocalizationProvider>
      </Stack>
      {showShortcut && (
        <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
          <Button variant="outlined" onClick={() => onShortcutClick('today')}>
            {t('Devices.label.today')}
          </Button>
          <Button variant="outlined" onClick={() => onShortcutClick('last7Days')}>
            {t('Devices.label.last7Days')}
          </Button>
          <Button variant="outlined" onClick={() => onShortcutClick('last30Days')}>
            {t('Devices.label.last30Days')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
